import { useTranslation } from "../context/Internationalization";
import { axiosGraphQL } from "../api";

/**
 * Build a standard Error with helpful properties:
 * - message: translated, concatenated messages
 * - code: first GraphQL extensions.code if present
 * - graphQLErrors: raw errors array (if provided)
 */
function buildGraphQLError(t, errors, specificMsg = null) {
  const list = Array.isArray(errors) ? errors : [errors];
  if (!list || list.length === 0) {
    const err = new Error(t("an_error_occurred"));
    return err;
  }

  // Prefer a specific message if provided
  if (specificMsg) {
    const err = new Error(specificMsg);
    return err;
  }

  // Translate + join messages
  const concat = list
    .map((e) => {
      // Try to parse JSON-encoded message; otherwise use raw
      let msg = e?.message ?? "";
      try {
        const parsed = JSON.parse(msg);
        msg = parsed?.message ?? msg;
      } catch {
        // ignore
      }
      return t(msg) || t("an_error_occurred");
    })
    .join(",\n");

  const first = list[0];
  const code =
    first?.extensions?.code ??
    (() => {
      // If we JSON-encoded the message, try again
      try {
        const parsed = JSON.parse(first?.message || "");
        return parsed?.extensions?.code;
      } catch {
        return undefined;
      }
    })();

  const err = new Error(concat);
  err.code = code;
  err.graphQLErrors = list;
  return err;
}

const useGraphql = () => {
  const { t } = useTranslation();

  const handleGraphql = (queryObject) =>
    axiosGraphQL(queryObject)
      .then((response) => {
        // GraphQL 200 with errors in payload
        if (response?.data?.errors) {
          throw buildGraphQLError(t, response.data.errors);
        }
        return response;
      })
      .catch((error) => {
        // Transport-level error with GraphQL errors in body
        const gqlErrors = error?.response?.data?.errors;
        if (gqlErrors && Array.isArray(gqlErrors) && gqlErrors.length) {
          throw buildGraphQLError(t, gqlErrors);
        }

        // Network / request setup / unknown
        const fallback = buildGraphQLError(
          t,
          [error],
          error?.message || t("an_error_occurred")
        );
        // Preserve HTTP status if present
        if (error?.response?.status) {
          fallback.response = error.response;
        }
        throw fallback;
      });

  return { axiosGraphQL: handleGraphql };
};

export default useGraphql;
